import { ROLE } from './acl$';
import { CoordinatorService } from './coordinator.service';
import { MemberCloudFile } from './member-cloud-file';
import { ProjectTeam } from './project-team';
import { BinaryTree } from './binary-tree';
import { BinaryTreeNode } from './binary-tree-node';
import { ScriptUser } from './script-user';

type CHECK_DIR_ENTRY = {
    dH: FileSystemDirectoryHandle;
    fLs?: FileSystemFileHandle[];
    fH?: FileSystemFileHandle;
};

type UPLOAD_PROBLEM_ENTRY = {
    dH: FileSystemDirectoryHandle;
    fH: FileSystemFileHandle;
    timestamp: number;
};

export class ProjectMember {
    // private _cloudSync!: CloudSync
    public coords: CoordinatorService;

    private _uploadProblemTimeLimit = -10 * 60 * 1000; // 10 minutes

    // private _isFirstCloudResult = true
    // private _firstTimeout: any

    private _uploadProblemMap = new Map<string, UPLOAD_PROBLEM_ENTRY>();

    private _isOn = true;

    isReady = false;

    private _id2BtMap = new Map<string, Map<BinaryTree, Set<string>>>();

    // private _commentLs: Comment[] = [];

    private _cacheH!: FileSystemDirectoryHandle;

    private _cloudFileMap = new Map<string, MemberCloudFile>();
    private __message: string = '';

    private _canAttach = false;

    initUploadDirMap = new Map<string, any>();

    constructor(
        public projectTeam: ProjectTeam,
        public id: string,
        public projectId: string,
        private _role: ROLE,
        private _isPending: boolean,
    ) {
        this.coords = projectTeam.coords;
        if (_role === 'owner') this._canAttach = true;
        if (!this.user) this.coords.user$.updateUser(this.id);
    }

    async _initCacheH() {
        const r = await navigator.storage.getDirectory();
        const pH = await r.getDirectoryHandle(this.projectId, { create: true });

        if (this.id === this.coords.user$.currentId) this._cacheH = pH;
        else {
            const tH = await pH.getDirectoryHandle('_team', { create: true });
            this._cacheH = await tH.getDirectoryHandle(this.id, { create: true });
        }
    }

    get commentLs() {
        return this.coords.comment$.userCommentLs(this.id);
    }

    public get isOn() {
        return this._isOn;
    }
    public set isOn(value) {
        this._isOn = value;
        this.coords.selection$.clearButtonAction();
    }

    get name() {
        return this.user?.displayName || '???';
    }

    get icon() {
        return this.coords.icon('user');
    }

    setOption(opt: string, value: boolean) {
        switch (opt) {
            case 'canAttach':
                this._canAttach = value;
                return;
        }
    }

    async listenCloud() {
        await this._initCacheH();
        if (this.isCurrentUser) await this._checkUploadDir();

        const uns = await this.coords.firebase$.firestore.listenQuery(
            'storage',
            [
                { field: 'userId', value: this.id },
                { field: 'projectId', value: this.projectId },
            ],
            async (params) => {
                if (this.isReady && this.isCurrentUser) return;
                const mcfLs: MemberCloudFile[] = [];
                let lastChange = 0;

                params.docChanges().map(async (itm: any) => {
                    const d = itm.doc.data();

                    if (this.isReady && d._instId === this.coords.instanceId) return;

                    if (!d.hash) return;
                    const fileId = d.remotePath.split('/').pop();

                    switch (itm.type) {
                        case 'added':
                        case 'modified':
                            if (!this._testCloudFilename(fileId)) return;
                            const mcf = this.getOrCreateCloudFile(fileId);
                            if (!mcf) return;
                            mcf.updateFromCloudData(d);
                            mcfLs.push(mcf);
                            const mcfChange = mcf.remoteEpochModified;
                            if (mcfChange > lastChange) lastChange = mcfChange;
                        // return mcf.synchronize()
                        case 'removed':
                            return this._removeOther(d);
                    }
                });

                if (!this.isReady) {
                    await this._checkLocalChache();

                    for (const [k, v] of Array.from(this._uploadProblemMap.entries())) {
                        const mfc = this._cloudFileMap.get(k);
                        if (
                            mfc &&
                            v.timestamp - mfc.remoteEpochModified > this._uploadProblemTimeLimit
                        ) {
                            mfc.setUploadProblem(v);
                        } else if (v.timestamp - lastChange > this._uploadProblemTimeLimit) {
                            const missingMfc = new MemberCloudFile(this, k);
                            missingMfc.setUploadProblem(v);
                            this._cloudFileMap.set(k, missingMfc);
                            mcfLs.push(missingMfc);
                        } else
                            try {
                                await v.dH.removeEntry(v.fH.name);
                            } catch (e) {}
                    }
                }

                await Promise.all(mcfLs.map((mcf) => mcf.synchronize()));

                if (!this.projectTeam.isReady) {
                    this.isReady = true;
                    await this.projectTeam.tryLoad();
                }
            },
        );
        uns && this.coords.user$.addQuerySubscription(this._listenStorageTag, uns);
    }

    private async _checkLocalChache() {
        const mcfLs: MemberCloudFile[] = [];
        const deleteSet = new Set<string>();
        const hashMap = new Map<string, string>();

        for await (const e of (this._cacheH as any).values()) {
            if (e.kind !== 'file') continue;

            if (!e.name.startsWith('.')) {
                const mcf = this._cloudFileMap.get(e.name);
                mcf ? mcfLs.push(mcf) : deleteSet.add(e.name);
            } else if (e.name.startsWith('.hash_')) {
                const i = e.name.lastIndexOf('_');
                hashMap.set(e.name.substring(6, i), e.name.substring(i + 1));
                deleteSet.add(e.name);
            }
        }
        mcfLs.forEach((mcf) => {
            const localH = hashMap.get(mcf.id);
            if (localH) {
                mcf.setLocalHash(localH);
                deleteSet.delete(`.hash_${mcf.id}_${localH}`);
            }
        });

        for (const fn of deleteSet) {
            await this.deleteLocalFile(fn);
        }
    }

    private async _checkUploadDir() {
        this._uploadProblemMap.clear();
        const allFileLs: CHECK_DIR_ENTRY[] = [];
        try {
            const uploadH = await this._cacheH.getDirectoryHandle('_uploads2');

            for await (const dE of (uploadH as any).values()) {
                if (dE.kind !== 'directory') continue;
                const entryLs: FileSystemFileHandle[] = [];
                for await (const entry of (dE as any).values()) {
                    entryLs.push(entry);
                }
                entryLs.length && allFileLs.push({ dH: dE, fLs: entryLs });
            }
        } catch (e) {}

        if (allFileLs.length) {
            const deleteLs: CHECK_DIR_ENTRY[] = [];

            this.coords.utils$.consoleInfo({
                tag: '!!!!! UPLOAD FILES NOT FINALIZED!!!!',
                color: 'red',
                background: 'yellow',
                isGroup: true,
            });

            allFileLs.forEach(({ dH, fLs }) => {
                fLs = fLs!;
                fLs.sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
                const fH = fLs!.pop()!;
                this._uploadProblemMap.set(dH.name, {
                    dH,
                    fH,
                    timestamp: parseInt(fH.name, 10),
                });
                fLs.length && deleteLs.push({ dH: dH, fLs: fLs });
            });
            try {
                deleteLs.forEach(async ({ dH, fLs }) => {
                    fLs?.forEach(async (fH) => {
                        await dH.removeEntry(fH.name);
                    });
                });
            } catch (e) {}

            if (this._uploadProblemMap.size)
                this.coords.utils$.consoleInfo({
                    tag: 'UPLOAD PROBLEM MAP',
                    color: 'red',
                    background: 'yellow',
                    isGroup: true,
                    payload: this._uploadProblemMap,
                });
            console.groupEnd();
        }
    }

    async deleteLocalFile(fn: string) {
        try {
            await this._cacheH.removeEntry(fn);
        } catch (e) {}
    }

    private get _listenStorageTag() {
        return `storage-${this.id}`;
    }

    // async checkUploads() {
    //     this._localCache.checkUploadFilesDir()
    // }

    otherBt(
        itmId: string,
        bt: BinaryTree,
    ):
        | {
              pm: ProjectMember;
              nodeSet: Set<string>;
          }
        | undefined {
        if (this.isCurrentUser || !this.isOn) return undefined;
        const s = this._id2BtMap.get(itmId)?.get(bt);
        if (!s) return undefined;
        return {
            pm: this,
            nodeSet: s,
        };
    }

    get btMap() {
        const mp = new Map<string, Map<string, string[]>>();
        this._id2BtMap.forEach((btMap, itmId) => {
            let idMp = mp.get(itmId);
            if (!idMp) {
                idMp = new Map<string, string[]>();
                mp.set(itmId, idMp);
            }
            btMap.forEach((s, bt) => {
                idMp.set(bt.id, Array.from(s));
            });
        });

        if (this.isCurrentUser) return this.coords.binaryTree$.btMap;
        return mp;
    }

    async afterCloudDownload(mcf: MemberCloudFile) {
        // const localF = await this._localCache.afterCloudDownload(mcf)
        if (!this.coords.project$.current) return;
        switch (mcf.type) {
            case 'comment':
                this._refreshOtherCommentsRun(mcf);
                break;
            case 'bt-storage':
                this._refreshOtherBTRun(mcf);
                break;
            case 'bt':
                this._refreshBT(mcf);
                // this._refreshOtherBTRun(mcf)
                break;
            case 'dataset-xitemLs':
                this._refreshMain(mcf);
                break;
            case 'project':
                this._refreshProject(mcf);
                break;
            case 'attachment':
                this._refreshAttachments(mcf);
                break;
            case 'view':
                this._refreshView(mcf);
                break;
            default:
                console.warn('unknown file type', this.role, mcf.type);
        }
        this.coords.display$.markForCheck();
    }

    activeIdSet(btn: BinaryTreeNode, s: Set<string>) {
        if (this.isCurrentUser || !this.isOn) return;
        this._id2BtMap.forEach((mp, itmId) => {
            if (mp.get(btn.binaryTree)?.has(btn.id)) s.add(itmId);
        });
    }

    get isCurrentUser() {
        return this.id === this.coords.user$.currentId;
    }

    get isCurrent() {
        return this.id === this.coords.user$.currentId;
    }

    get isOwner() {
        return this.role === 'owner';
    }

    getOrCreateCloudFile(fileId: string): MemberCloudFile | undefined {
        let f = this._cloudFileMap.get(fileId);
        if (!f) f = this.createCloudFile(fileId);
        return f;
    }

    getCloudFile(fileId: string): MemberCloudFile | undefined {
        return this._cloudFileMap.get(fileId);
    }

    createCloudFile(fileId: string): MemberCloudFile | undefined {
        if (fileId.startsWith('project_') && fileId !== `project_${this.projectTeam.id}`) {
            this.coords.utils$.consoleError(
                'project file name mismatch',
                new Error(`fileId ${fileId} FOR PROJECT id  ${this.projectTeam.id}`),
            );
            return;
        }

        const f = new MemberCloudFile(this, fileId);
        this._cloudFileMap.set(f.id, f);
        return f;
    }

    get cloudFileLs() {
        return Array.from(this._cloudFileMap.values());
    }

    private _testCloudFilename(fn: string) {
        if (this.isCurrentUser) return true;

        if (this.isOwner) {
            if (
                fn.startsWith('project_') ||
                fn.startsWith('dataset-xitemLs_') ||
                fn.startsWith('team_') ||
                fn.startsWith('bt_') ||
                fn.startsWith('keyword_keywordLs_') ||
                fn.startsWith('style_')
            )
                return true;
        }

        if (fn.startsWith('comment_') || fn.startsWith('bt-storage_')) return true;

        if (fn.startsWith('view_') && !fn.startsWith('view__')) return true;

        if (fn.startsWith('attachment_')) return true;
        if (fn.startsWith('auto-user')) return true;

        return false;
    }

    async _removeOther(params: any) {
        if (this.isCurrentUser) return;
        const fn = params.remotePath.split('/').pop();
        const i = fn.indexOf('_');
        const tp = fn.slice(0, i);
        const targetId = fn.slice(i + 1);
        switch (tp) {
            case 'bt':
                const bt = this.coords.binaryTree$.getTree(targetId);
                bt && this.coords.binaryTree$.delete(bt);
                return;
        }
        const mfc = this._cloudFileMap.get(targetId);
        mfc && this._deleteFile(mfc);
    }

    async refreshOther() {
        if (this.isCurrentUser) return;
        await this.refreshOtherComments();
        await this.refreshOtherBT();
    }

    async _refreshBT(mcf: MemberCloudFile) {
        if (!this.isOwner) return;
        try {
            const params = await this._loadFile(mcf);
            if (!params) {
                return;
            }
            params.ownerId = this.id;
            this.coords.binaryTree$.load3([params]);
            this.refreshOtherBT();
            this.coords.display$.markForCheck();
        } catch (e) {
            this.coords.utils$.consoleError('PM REFRESH BT ERROR', e);
        }
    }

    async _refreshProject(mcf: MemberCloudFile) {
        const params = await this._loadFile(mcf);
        params.ownerId = this.id;
        const pr = this.coords.project$.current!;
        Object.keys(params).forEach((k) => {
            pr.set(k, params[k]);
        });
        this.coords.display$.markForCheck();
        // this.coords.dataset$.load3(params)
        // this.coords.stage$.datasetTag === "main" &&
        //     alert(this.coords.text("collectionWasChanged"))
    }

    async _refreshView(mcf: MemberCloudFile) {
        const params = await this._loadFile(mcf);
        params.ownerId = this.id;
        this.coords.view$.load3([params]);
        this.coords.display$.markForCheck();
    }

    async _refreshAttachments(mcf: MemberCloudFile) {
        const params = await this._loadFile(mcf);
        params.ownerId = this.id;
        this.coords.attachment$.load3(params);
        this.coords.display$.markForCheck();
    }

    async _refreshMain(mcf: MemberCloudFile) {
        const params = await this._loadFile(mcf);
        params.ownerId = this.id;
        this.coords.dataset$.load3(params);
        this.coords.stage$.datasetTag === 'main' && alert(this.coords.text('collectionWasChanged'));
    }
    async _refreshOtherCommentsRun(mcf: MemberCloudFile) {
        if (this.isCurrentUser) return;
        try {
            this.coords.comment$.resetProjectMember(this.id);
            const params = await this._loadFile(mcf);
            params.ownerId = this.id;
            const resp = this.coords.comment$.load3(params);

            const cLs = resp?.otherLs;
            cLs && this.coords.comment$.addProjectMemberComments(cLs);
            this.coords.display$.markForCheck();
        } catch (e) {
            console.error('refresh other comments run', e);
        }
    }
    async refreshOtherComments() {
        try {
            for (const mcf of Array.from(this._cloudFileMap.values())) {
                if (mcf.type === 'comment') {
                    return this._refreshOtherCommentsRun(mcf);
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async _loadFile(mcf: MemberCloudFile) {
        try {
            const fH = await this._cacheH.getFileHandle(mcf.id);
            const f = await fH.getFile();
            return JSON.parse(await f.text());
        } catch (e) {
            this.coords.utils$.consoleError('LOAD FILE ERROR ' + mcf.id, e);
            return {};
        }
    }

    private async _deleteFile(mcf: MemberCloudFile) {
        try {
            await this._cacheH.removeEntry(mcf.id);
            await this._cacheH.removeEntry(`.hash_${mcf.id}_${mcf.getHash()}`);
        } catch (e) {
            this.coords.utils$.consoleError('DELETE FILE ERROR ' + mcf.id, e);
        }
    }

    async _refreshOtherBTRun(mcf: MemberCloudFile) {
        if (this.isCurrentUser) return;
        try {
            const params = await this._loadFile(mcf);
            params.ownerId = this.id;
            const resp = this.coords.binaryTree$.load3storage([params]);

            const btStorageLs = resp?.otherLs;
            btStorageLs && this._parseOtherBtLs(btStorageLs);
            // cLs && this.coords.comment$.addProjectMemberComments(cLs)
            this.coords.display$.markForCheck();
        } catch (e) {
            console.error('refresh other comments run', e);
        }
    }
    async refreshOtherBT() {
        if (this.isCurrentUser) return;
        for (const mcf of Array.from(this._cloudFileMap.values())) {
            try {
                if (mcf.type !== 'bt-storage') continue;
                this._refreshOtherBTRun(mcf);
            } catch (e) {
                console.error(e);
            }
        }
    }

    public get canAttach() {
        return this._canAttach;
    }

    public set canAttach(value: boolean) {
        if (this._canAttach === value) return;
        this._canAttach = value;
        this.coords.user$.cloudTeamManager?.changeTeamOption(this, 'canAttach', value);
    }

    public get role(): ROLE {
        return this._role;
    }
    public set role(value: ROLE) {
        if (this._role === value) return;
        this._role = value;
        this.coords.user$.cloudTeamManager?.changeTeamRole(this, value);
    }

    get user() {
        return this.coords.user$.get(this.id);
    }

    get isPending() {
        return this._isPending;
    }

    set isPending(value: boolean) {
        this._isPending = value;
    }

    async terminate() {
        await this.coords.user$.closeQuerySubscription(this._listenStorageTag);
    }

    get localFilepath() {
        let pth = `/${this.projectId}`;
        if (this.id === this.coords.user$.currentId) return pth;
        return `${pth}/_team/${this.id}`;
    }

    async getLocalFileHandle(fileId: string) {
        return await this._cacheH.getFileHandle(fileId, { create: true });
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => this.delete(),
        };
    }

    delete() {
        this._delete();
    }

    private async _delete() {
        const projectId = this.coords.project$.currentId;
        const id = `${this.id}|${projectId}`;
        const resp = await this.coords.firebase$.firestore.deleteDocument('teams', id);

        this.projectTeam.removeMember(this);
        this.coords.display$.markForCheck();
        return resp;
    }

    private _parseOtherBtLs(ls: any[]) {
        ls.forEach((x: any) => {
            const bt = x.bt as BinaryTree;
            const bts = x.bts;

            this._id2BtMap.forEach((mp) => {
                mp.delete(bt);
            });
            Object.keys(bts).forEach((nodeId) => {
                const idLs = bts[nodeId];
                idLs.forEach((id: string) => {
                    let mp = this._id2BtMap.get(id);
                    if (!mp) {
                        mp = new Map<BinaryTree, Set<string>>();
                        this._id2BtMap.set(id, mp);
                    }
                    let s = mp.get(bt);
                    if (!s) {
                        s = new Set<string>();
                        mp.set(bt, s);
                    }
                    s.add(nodeId);
                });
            });
        });
    }

    async processOtherLoad(params: any) {
        const resp = await params;
        const btStorages = resp.btStorages?.otherLs || [];
        const comments = resp.comments?.otherLs || [];
        this.coords.comment$.addProjectMemberComments(comments);
        this._parseOtherBtLs(btStorages);
    }

    async load3() {
        const mp = await this.coords.utils$.filehandleMap(this._cacheH);

        const pr = await this.coords.utils$.load3(mp, this.id, this.isOwner);
        return pr;
    }

    get message() {
        return this._message;
    }

    public get _message(): string {
        return this.__message;
    }
    public set _message(value: string) {
        this.__message = value;
        this.coords.display$.markForCheck();
    }

    async deleteUpdate(ls: string[]) {
        for (const fileId of ls) {
            const f = this._cloudFileMap.get(fileId);
            f?.delete();
        }
    }

    get scriptClass() {
        return new ScriptUser(this.coords, this);
    }
}
